import React, {
  Dispatch,
  FormEvent,
  useEffect,
  useState,
  useMemo,
} from "react";
import GoogleLogoIcon from "src/assets/icons/google_logo.svg?react";
import MSWindowsLogoIcon from "src/assets/icons/microsoft-window.svg?react";
import CleverLogoIcon from "src/assets/icons/clever.svg?react";
import Divider from "@src/components/divider";
import styles from "./login.module.scss";
import { useLoginMutation } from "../../hooks/useLoginMutation";
import {
  TextInput,
  TextInputValue,
} from "../../components/text-input/TextInput";
import NewAuthPageLayout from "../../components/auth-page-layout/NewAuthPageLayout";
import { Link } from "react-router-dom";
import ArrowIcon from "../../assets/icons/curvearrow.svg?react";
import {
  isEmptyObject,
  validateEmail,
  validatePassword,
} from "@src/utils/validation";
import useGoogleAuth from "@src/hooks/useGoogleAuth";
import useMicrosoftAuth from "@src/hooks/useMicrosoftAuth";
import useCleverAuth from "@src/hooks/useCleverAuth";
import Button from "@src/components/button/Button";
import { SESSION_TIMED_OUT } from "@src/hooks/useAuthToken";
import Spinner from "@src/components/spinner/Spinner";
import { useToast } from "@src/hooks/useToast";
import { useQueryParams } from "@src/hooks";

type Props = {};

const emptyErrors = {
  email: "",
  password: "",
};
const ERROR_MESSAGES = {
  NOT_REGISTERED:
    <div>Please check your email to complete your account registration. <br/><br/> Once this is done, you will be able to sign in.</div>,
  INACTIVE: "Your account has been disabled",
  MAX_ATTEMPTS_LOGIN_FAILED:
    "You have reached the maximum amount of login attempts. Please contact your account administrator to unlock your account.",
  EMAIL_LOGIN_DISABLED:
    "Please log in using Single Sign-On. This is the only login method your school permits.",
  EXPIRED:
    "Your school’s account is no longer active. If you believe this is an error, please contact our support team using the Live Chat.",
  SCHOOL_DISABLED: "Your school is disabled.",
  AUTH_FAILED: "Your username or password is incorrect.",
  SSO_AUTH_FAILED: "The username or password you entered is incorrect.",
  NO_ACCOUNT: "The username or password you entered is incorrect.",
  INVALID_CLEVER_ACCOUNT:
    "There is no account associated with this email address. You have to sign in with the same that you were invited with. Please go to Clever.com and sign yourself out. Or alternatively ask your manager to invite you with the same email address that you used with Clever.com",
};

const getErrorMessage = (msg?: string) => {
  if (!msg) return "";
  return ERROR_MESSAGES[msg] ?? msg;
};

function Login(props: Props) {
  const [login, , { loading, error }] = useLoginMutation();
  const { googleAuth, googleAuthLoading, googleAuthError } =
    useGoogleAuth("Login");
  const { microsoftAuth, microsoftAuthLoading, microsoftError } =
    useMicrosoftAuth("Login");
  const { cleverAuth, cleverAuthLoading, cleverError } = useCleverAuth("Login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState(emptyErrors);
  const [sessionTimeOut, setSessionTimeOut] = useState(false);
  const { warningToast } = useToast();

  const resetPassword = useQueryParams().get('resetPassword');

  useEffect(() => {
    const sessionTimeOut = localStorage.getItem(SESSION_TIMED_OUT);
    if (sessionTimeOut) {
      setSessionTimeOut(true);
      warningToast("You have been logged out.");
    }
  }, [warningToast]);

  const handleChange =
    (setter: Dispatch<string>, key: keyof typeof emptyErrors) =>
    (value: TextInputValue) => {
      setErrors({
        ...errors,
        [key]: "",
      });
      setter(value as string);
    };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const errors: any = {};

    if (validateEmail(email)) {
      errors.email = "Please enter a valid email address.";
    }

    if (validatePassword(password)) {
      errors.password = validatePassword(password);
    }
    if (isEmptyObject(errors)) {
      setErrors(emptyErrors);
      login(email, password);
    } else {
      setErrors(errors);
    }
  };

  const titleView = useMemo(() => {
    if (sessionTimeOut) {
      return (
        <div className={styles.timeOutView}>
          <h3>Your session has timed out.</h3>
          <span>Please Sign In again</span>
        </div>
      );
    }
    return <div style={{ marginBottom: 20 }}>Sign In to your Account</div>;
  }, [sessionTimeOut]);

  const authErrorMsg = useMemo(() => {
    if (
      loading ||
      googleAuthLoading ||
      microsoftAuthLoading ||
      cleverAuthLoading
    ) {
      return "";
    }
    return getErrorMessage(
      googleAuthError?.message ||
        microsoftError?.message ||
        error?.message ||
        cleverError?.message
    );
  }, [
    error?.message,
    googleAuthError?.message,
    googleAuthLoading,
    loading,
    microsoftAuthLoading,
    microsoftError?.message,
    cleverError?.message,
    cleverAuthLoading,
  ]);

  return (
    <NewAuthPageLayout
      onSubmit={handleSubmit}
      loading={loading}
      title={titleView}
      buttonTitle={"Log in"}
      disableLogin={password.length === 0}
    >
      {authErrorMsg && <div className={styles.gError}>{authErrorMsg}</div>}
      {resetPassword &&
        <div className={styles.resetBox}>
          <div className={styles.title}>Your Password has been Reset.</div>
          <div className={styles.detail}>Please sign in below.</div>
        </div>}
      <TextInput
        allowSpecialCharacters
        className={styles.input}
        onChange={handleChange(setEmail, "email")}
        value={email}
        label={"Email*"}
        placeholder={"Email"}
        id={"email"}
        hasError={!!errors.email}
        subTitle={errors.email}
      />
      <TextInput
        allowSpecialCharacters
        className={styles.input}
        onChange={handleChange(setPassword, "password")}
        value={password}
        label={"Password*"}
        placeholder={"Password"}
        id={"password"}
        type={"password"}
        hasError={!!errors.password}
        subTitle={errors.password}
      />
      <div className={styles.actionsWrapper}>
        <Link className={styles.link} to={"/forgot-password"}>
          Reset Password
        </Link>
        <div className={styles.arrowDiv}>
          <span className={styles.arrowText}>
            Need help logging in?
            <br />
            Click to Chat with us!
          </span>
          <ArrowIcon width={40} height={200} className={styles.arrowIcon} />
        </div>
      </div>
      <Button
        disabled={loading || password.length === 0}
        className={styles.submit}
        type={"submit"}
        label={"Sign In"}
        isLoading={loading}
      />
      <div className={styles.divider}>
        <Divider label="Or" />
      </div>
      <div
        className={styles.gInput}
        role="button"
        aria-pressed="false"
        onClick={() => {
          if (!googleAuthLoading) googleAuth();
        }}
      >
        <div className={styles.iconBackground}>
          <div className={styles.iconView}>
            <GoogleLogoIcon />
          </div>
        </div>
        <span className={styles.gLabel}>
          {googleAuthLoading ? (
            <Spinner width={20} height={20} />
          ) : (
            "Sign In with Google"
          )}
        </span>
      </div>
      <div
        className={styles.msInput}
        role="button"
        aria-pressed="false"
        onClick={() => {
          if (!microsoftAuthLoading) microsoftAuth();
        }}
      >
        <div className={styles.iconBackground}>
          <div className={styles.iconView}>
            <MSWindowsLogoIcon width={18} />
          </div>
        </div>
        <span className={styles.msLabel}>
          {microsoftAuthLoading ? (
            <Spinner width={20} height={20} />
          ) : (
            "Sign In with Microsoft"
          )}
        </span>
      </div>
      <div
        className={styles.cleverInput}
        role="button"
        aria-pressed="false"
        onClick={() => {
          if (!cleverAuthLoading) cleverAuth();
        }}
      >
        <div className={styles.iconBackground}>
          <div className={styles.iconView}>
            <CleverLogoIcon />
          </div>
        </div>
        <span className={styles.cleverLabel}>
          {cleverAuthLoading ? (
            <Spinner width={20} height={20} />
          ) : (
            "Sign In with Clever"
          )}
        </span>
      </div>
    </NewAuthPageLayout>
  );
}

export default Login;
